import React, { Component } from "react";
import deepCopy from "./Util/DeepCopy";
import {
  AnimatedError,
  Button,
  FIELD_BOX_CLASS,
  FIELD_LABEL_CLASS,
  MultiSelectDropdown,
  REQUIRED_MARK_CLASS,
  SelectChevron,
  TEXTAREA_MIN_HEIGHT_CLASS,
  fieldClass,
} from '@charitystack/shared';
import { getPageTransitionClasses } from "./Util/pageTransition";
import FormStepFrame from "./Components/FormStepFrame";
import {
  areCustomInputPagesEqual,
  buildCustomInputPages,
  findCustomInputPageIndex,
} from "./Util/customInputPagination";

const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// A11-003: per-instance id base for label targets. The overlay mounts the form
// twice, so an id built from the question alone would repeat and bind a label
// to the hidden copy.
let customInputsInstanceCount = 0;

class CustomInputsPage extends Component {
  constructor(props) {
    super(props);
    customInputsInstanceCount += 1;
    this.fieldIdBase = `cs-custom-input-${customInputsInstanceCount}`;

    this.state = {
      fieldErrors: {},
      hasAnyErrors: false,
      disabled: false,
      brandAccent5: null,
      brandAccent10: null,
      brandAccent20: null,
      brandAccent40: null,
      brandAccent50: null,
      brandAccent60: null,
      // Safari fix: remount key per select index — forces React to destroy
      // and recreate the <select> DOM node, resetting Safari's stale internal
      // focus state that causes the double-click-to-reopen bug.
      selectKeys: {},
      fieldPages: [],
      currentFieldPageIndex: 0,
      wasPageActive: false,
      internalPageDirection: null,
      activeTransitionSource: null,
    };

    this.pageBodyRef = React.createRef();
    this.footerRef = React.createRef();
    this.measurementRootRef = React.createRef();
    this.fieldMeasurementRefs = {};
    this.paginationResizeObserver = null;
    this.paginationAnimationFrame = null;
    this.pendingFocusFieldIndex = null;
  }

  componentDidMount() {
    this.extractBrandColors();
    this.refreshPaginationObserver();
    this.schedulePaginationRecompute();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.wasPageActive && nextProps.isPageActive && nextProps.pageTransitionDirection) {
      return {
        wasPageActive: true,
        activeTransitionSource: "external",
      };
    }

    if (prevState.wasPageActive && !nextProps.isPageActive) {
      return {
        wasPageActive: false,
        activeTransitionSource: null,
      };
    }

    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.resetPaginationKey !== this.props.resetPaginationKey) {
      this.pendingFocusFieldIndex = null;
      this.setState(
        {
          fieldErrors: {},
          hasAnyErrors: false,
          selectKeys: {},
          fieldPages: [],
          currentFieldPageIndex: 0,
          wasPageActive: false,
          internalPageDirection: null,
          activeTransitionSource: null,
        },
        () => {
          this.schedulePaginationRecompute();
        },
      );
      return;
    }

    if (prevProps.brandColors !== this.props.brandColors) {
      this.extractBrandColors();
    }

    const shouldRefreshPaginationObserver =
      prevProps.enablePagination !== this.props.enablePagination ||
      prevProps.inline !== this.props.inline ||
      prevState.currentFieldPageIndex !== this.state.currentFieldPageIndex;

    if (shouldRefreshPaginationObserver) {
      this.refreshPaginationObserver();
    }

    const shouldRecomputePagination =
      prevProps.customInputsValues !== this.props.customInputsValues ||
      prevProps.enablePagination !== this.props.enablePagination ||
      prevProps.inline !== this.props.inline ||
      prevProps.formDisplayMode !== this.props.formDisplayMode ||
      prevProps.isDrawerOpen !== this.props.isDrawerOpen ||
      prevState.currentFieldPageIndex !== this.state.currentFieldPageIndex ||
      prevState.fieldErrors !== this.state.fieldErrors;

    if (shouldRecomputePagination) {
      this.schedulePaginationRecompute();
    }
  }

  componentWillUnmount() {
    this.teardownPaginationObserver();

    if (this.paginationAnimationFrame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.paginationAnimationFrame);
    }
  }

  shouldPaginate = () => Boolean(this.props.enablePagination && !this.props.inline);

  getAllFieldIndexes = () => this.props.customInputsValues.map((_, index) => index);

  getResolvedFieldPages = () => {
    if (this.state.fieldPages.length > 0) {
      return this.state.fieldPages;
    }

    const allFieldIndexes = this.getAllFieldIndexes();
    return allFieldIndexes.length > 0 ? [allFieldIndexes] : [];
  };

  getCurrentPageFieldIndexes = () => {
    const fieldPages = this.getResolvedFieldPages();

    if (fieldPages.length === 0) {
      return [];
    }

    const pageIndex = Math.min(
      this.state.currentFieldPageIndex,
      Math.max(fieldPages.length - 1, 0),
    );

    return fieldPages[pageIndex] || [];
  };

  isLastFieldPage = () => {
    const fieldPages = this.getResolvedFieldPages();
    return this.state.currentFieldPageIndex >= fieldPages.length - 1;
  };

  goToPreviousFieldPage = () => {
    if (!this.shouldPaginate() || this.state.currentFieldPageIndex === 0) {
      return false;
    }

    this.setState(
      (prevState) => ({
        currentFieldPageIndex: Math.max(prevState.currentFieldPageIndex - 1, 0),
        internalPageDirection: "back",
        activeTransitionSource: "internal",
      }),
      () => {
        this.schedulePaginationRecompute();
      },
    );

    return true;
  };

  refreshPaginationObserver = () => {
    this.teardownPaginationObserver();

    if (!this.shouldPaginate() || typeof ResizeObserver === "undefined") {
      return;
    }

    this.paginationResizeObserver = new ResizeObserver(() => {
      this.schedulePaginationRecompute();
    });

    if (this.pageBodyRef.current) {
      this.paginationResizeObserver.observe(this.pageBodyRef.current);
    }

    if (this.footerRef.current) {
      this.paginationResizeObserver.observe(this.footerRef.current);
    }

    if (this.measurementRootRef.current) {
      this.paginationResizeObserver.observe(this.measurementRootRef.current);
    }
  };

  teardownPaginationObserver = () => {
    if (this.paginationResizeObserver) {
      this.paginationResizeObserver.disconnect();
      this.paginationResizeObserver = null;
    }
  };

  schedulePaginationRecompute = () => {
    if (this.paginationAnimationFrame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.paginationAnimationFrame);
    }

    if (typeof requestAnimationFrame === "function") {
      this.paginationAnimationFrame = requestAnimationFrame(() => {
        this.paginationAnimationFrame = null;
        this.recomputePagination();
      });
      return;
    }

    this.paginationAnimationFrame = null;
    this.recomputePagination();
  };

  recomputePagination = () => {
    const allFieldIndexes = this.getAllFieldIndexes();

    if (allFieldIndexes.length === 0) {
      if (this.state.fieldPages.length > 0 || this.state.currentFieldPageIndex !== 0) {
        this.setState({
          fieldPages: [],
          currentFieldPageIndex: 0,
        });
      }
      return;
    }

    const fallbackPages = [allFieldIndexes];

    if (!this.shouldPaginate()) {
      if (
        !areCustomInputPagesEqual(this.state.fieldPages, fallbackPages) ||
        this.state.currentFieldPageIndex !== 0
      ) {
        this.setState({
          fieldPages: fallbackPages,
          currentFieldPageIndex: 0,
        });
      }
      return;
    }

    const bodyHeight =
      this.pageBodyRef.current?.clientHeight ||
      this.pageBodyRef.current?.offsetHeight ||
      0;
    const footerHeight =
      this.footerRef.current?.offsetHeight ||
      this.footerRef.current?.getBoundingClientRect?.().height ||
      0;
    const availableHeight = Math.max(bodyHeight - footerHeight, 0);
    if (availableHeight <= 0) {
      return;
    }

    const fieldHeights = allFieldIndexes.map((index) => {
      const measurementNode = this.fieldMeasurementRefs[index];
      return (
        measurementNode?.offsetHeight ||
        measurementNode?.scrollHeight ||
        measurementNode?.getBoundingClientRect?.().height ||
        0
      );
    });

    if (fieldHeights.some((height) => height <= 0)) {
      return;
    }

    const nextFieldPages = buildCustomInputPages({
      fieldHeights,
      availableHeight,
    });

    const resolvedCurrentPages = this.getResolvedFieldPages();
    const currentPage = resolvedCurrentPages[this.state.currentFieldPageIndex] || resolvedCurrentPages[0] || [];
    const currentAnchorFieldIndex = currentPage[0] ?? 0;
    const targetFieldIndex =
      this.pendingFocusFieldIndex !== null ? this.pendingFocusFieldIndex : currentAnchorFieldIndex;
    const nextPageIndex = findCustomInputPageIndex(nextFieldPages, targetFieldIndex);

    this.pendingFocusFieldIndex = null;

    if (
      !areCustomInputPagesEqual(this.state.fieldPages, nextFieldPages) ||
      this.state.currentFieldPageIndex !== nextPageIndex
    ) {
      this.setState({
        fieldPages: nextFieldPages,
        currentFieldPageIndex: nextPageIndex,
      });
    }
  };

  getFieldErrorMessage = (customInput) => {
    if (!customInput?.required) {
      return null;
    }

    if (customInput.type === "MULTI_SELECTION_DROPDOWN") {
      const answers = customInput.answers || [];
      const validAnswers = answers.filter((answer) => answer && answer.trim() !== "");
      if (validAnswers.length === 0) {
        return `Please fill in ${customInput.title}`;
      }

      return null;
    }

    const answer = customInput.answers?.[0] || "";
    if (answer === "") {
      return `Please fill in ${customInput.title}`;
    }

    return null;
  };

  validateFields = (fieldIndexes = null) => {
    const indexesToValidate = Array.isArray(fieldIndexes)
      ? fieldIndexes
      : this.getAllFieldIndexes();
    const nextFieldErrors = Array.isArray(fieldIndexes) ? { ...this.state.fieldErrors } : {};
    let firstErrorIndex = null;

    indexesToValidate.forEach((index) => {
      delete nextFieldErrors[index];
    });

    indexesToValidate.forEach((index) => {
      const customInput = this.props.customInputsValues[index];
      const errorMessage = this.getFieldErrorMessage(customInput);

      if (!errorMessage) {
        return;
      }

      nextFieldErrors[index] = errorMessage;

      if (firstErrorIndex === null || index < firstErrorIndex) {
        firstErrorIndex = index;
      }
    });

    if (firstErrorIndex !== null) {
      this.pendingFocusFieldIndex = firstErrorIndex;
    }

    this.setState({
      fieldErrors: nextFieldErrors,
      hasAnyErrors: Object.keys(nextFieldErrors).length > 0,
    });

    if (this.shouldPaginate()) {
      this.schedulePaginationRecompute();
    }

    return firstErrorIndex !== null;
  };

  hasErrors = (fieldIndexes = null) => this.validateFields(fieldIndexes);

  handleClick = () => {
    if (this.shouldPaginate() && !this.isLastFieldPage()) {
      const currentPageFieldIndexes = this.getCurrentPageFieldIndexes();

      if (this.hasErrors(currentPageFieldIndexes)) {
        return;
      }

      this.setState(
        (prevState) => ({
          currentFieldPageIndex: Math.min(
            prevState.currentFieldPageIndex + 1,
            Math.max(this.getResolvedFieldPages().length - 1, 0),
          ),
          internalPageDirection: "forward",
          activeTransitionSource: "internal",
        }),
        () => {
          this.schedulePaginationRecompute();
        },
      );
      return;
    }

    if (this.hasErrors()) {
      return;
    }

    this.props.handleDonate();
  };

  handleBackClick = () => {
    if (this.goToPreviousFieldPage()) {
      return;
    }

    this.props.setPage(this.props.backPage || "START_PAGE", 'back');
  };

  handleShortTextAnswerChange = (event, index) => {
    const newCustomInputsValues = deepCopy(this.props.customInputsValues);
    newCustomInputsValues[index].answers = [event.target.value];
    this.props.setCustomInputsValues(newCustomInputsValues);

    if (this.state.fieldErrors[index]) {
      this.clearFieldError(index);
    }
  };

  handleLongTextAnswerChange = (event, index) => {
    const newCustomInputsValues = deepCopy(this.props.customInputsValues);
    newCustomInputsValues[index].answers = [event.target.value];
    this.props.setCustomInputsValues(newCustomInputsValues);

    if (this.state.fieldErrors[index]) {
      this.clearFieldError(index);
    }
  };

  handleSingleSelectDropdownAnswerChange = (event, index) => {
    const select = event.target;
    const newCustomInputsValues = deepCopy(this.props.customInputsValues);
    newCustomInputsValues[index].answers = [event.target.value];
    this.props.setCustomInputsValues(newCustomInputsValues);

    if (this.state.fieldErrors[index]) {
      this.clearFieldError(index);
    }

    if (isSafari) {
      this.setState((prevState) => ({
        selectKeys: {
          ...prevState.selectKeys,
          [index]: (prevState.selectKeys[index] || 0) + 1,
        },
      }));
    }

    setTimeout(() => {
      select.blur();
    }, 0);
  };

  handleMultiSelectDropdownAnswerChange = (newValues, index) => {
    const newCustomInputsValues = deepCopy(this.props.customInputsValues);
    newCustomInputsValues[index].answers = newValues.length > 0 ? newValues : [];
    this.props.setCustomInputsValues(newCustomInputsValues);

    if (this.state.fieldErrors[index]) {
      this.clearFieldError(index);
    }
  };

  clearFieldError = (index) => {
    const newFieldErrors = { ...this.state.fieldErrors };
    delete newFieldErrors[index];

    this.setState({
      fieldErrors: newFieldErrors,
      hasAnyErrors: Object.keys(newFieldErrors).length > 0,
    });
  };

  extractBrandColors = () => {
    const { brandColors } = this.props;
    if (brandColors) {
      this.setState({
        brandAccent60: brandColors["accent-60"] || null,
        brandAccent50: brandColors["accent-50"] || null,
        brandAccent40: brandColors["accent-40"] || null,
        brandAccent20: brandColors["accent-20"] || null,
        brandAccent10: brandColors["accent-10"] || null,
        brandAccent5: brandColors["accent-5"] || null,
      });
    }
  };

  setFieldMeasurementRef = (index, node) => {
    if (node) {
      this.fieldMeasurementRefs[index] = node;
      return;
    }

    delete this.fieldMeasurementRefs[index];
  };

  renderField = (customInput, index, { measurement = false } = {}) => {
    const { fieldErrors } = this.state;
    const { brandColors } = this.props;
    const inputIdSuffix = measurement ? `measurement-${index}` : `${index}`;
    const inputValue = customInput.answers?.[0] || "";

    return (
      <div className="w-full">
        {customInput.type === "SINGLE_SELECTION_DROPDOWN" && (
          <div className="relative inline-block w-full">
            {/* A11-003: the title is the select's label, so the control has a name. */}
            <label
              htmlFor={`${this.fieldIdBase}-select-${inputIdSuffix}`}
              className={`block pb-1 break-words ${FIELD_LABEL_CLASS}`}
            >
              {customInput.title}
              {customInput.required && <span className={REQUIRED_MARK_CLASS}>&nbsp;*</span>}
            </label>
            <div className="relative">
              <select
                id={`${this.fieldIdBase}-select-${inputIdSuffix}`}
                aria-required={customInput.required || undefined}
                key={isSafari && !measurement ? this.state.selectKeys[index] || 0 : undefined}
                onChange={
                  measurement
                    ? () => {}
                    : (event) => {
                        this.handleSingleSelectDropdownAnswerChange(event, index);
                      }
                }
                // Donor redesign (CON-515): the shared field recipe (SPEC §C Inputs).
                // Unanswered, it shows a placeholder in the same tertiary ink as the multi-select's;
                // the options themselves keep primary ink.
                className={`block h-[var(--cs-input-height)] cursor-pointer appearance-none pl-[var(--cs-input-padding-x)] pr-10 ${fieldClass(Boolean(fieldErrors[index]))} ${inputValue === "" ? "!text-[color:var(--cs-text-tertiary)] [&>option]:text-[color:var(--cs-text-primary)]" : ""}`}
                style={{
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  appearance: "none",
                  backgroundImage: "none",
                  WebkitTextFillColor: "inherit",
                }}
                value={inputValue}
                tabIndex={measurement ? -1 : undefined}
                aria-hidden={measurement ? "true" : undefined}
              >
                {inputValue === "" && <option value="">Select an option</option>}
                {customInput.options.map((option, optionIndex) => (
                  <option key={optionIndex} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <SelectChevron inField />
            </div>
            <AnimatedError error={fieldErrors[index]} />
          </div>
        )}

        {customInput.type === "SHORT_TEXT_INPUT" && (
          <div className="relative">
            <div className={`pb-1 break-words ${FIELD_LABEL_CLASS}`}>
              {customInput.title}
              {customInput.required && <span className={REQUIRED_MARK_CLASS}>&nbsp;*</span>}
            </div>
            <div className="flex items-center rounded-md">
              <div className="relative w-full">
                <div className="relative">
                  <input
                    type="text"
                    name={`short-text-input-${inputIdSuffix}-${customInput.title}`}
                    id={`short-text-input-${inputIdSuffix}-${customInput.title}`}
                    aria-label={customInput.title}
                    aria-required={customInput.required || undefined}
                    className={`peer block ${FIELD_BOX_CLASS} ${fieldClass(Boolean(fieldErrors[index]))}`}
                    placeholder=" "
                    value={inputValue}
                    onChange={
                      measurement
                        ? undefined
                        : (event) => this.handleShortTextAnswerChange(event, index)
                    }
                    readOnly={measurement}
                    tabIndex={measurement ? -1 : undefined}
                    maxLength="280"
                  />
                </div>
              </div>
            </div>
            <AnimatedError error={fieldErrors[index]} />
          </div>
        )}

        {customInput.type === "LONG_TEXT_INPUT" && (
          <div className="relative">
            <div className={`pb-1 break-words ${FIELD_LABEL_CLASS}`}>
              {customInput.title}
              {customInput.required && <span className={REQUIRED_MARK_CLASS}>&nbsp;*</span>}
            </div>
            <div className="flex items-center rounded-md">
              <div className="relative w-full">
                <div className="relative">
                  <textarea
                    type="text"
                    name={`long-text-input-${inputIdSuffix}-${customInput.title}`}
                    id={`long-text-input-${inputIdSuffix}-${customInput.title}`}
                    aria-label={customInput.title}
                    aria-required={customInput.required || undefined}
                    className={`peer block resize-none px-[var(--cs-input-padding-x)] py-[var(--cs-space-3)] ${TEXTAREA_MIN_HEIGHT_CLASS} ${fieldClass(Boolean(fieldErrors[index]))}`}
                    placeholder=" "
                    value={inputValue}
                    onChange={
                      measurement
                        ? undefined
                        : (event) => this.handleLongTextAnswerChange(event, index)
                    }
                    readOnly={measurement}
                    tabIndex={measurement ? -1 : undefined}
                    maxLength="1000"
                  />
                </div>
              </div>
            </div>
            <AnimatedError error={fieldErrors[index]} />
          </div>
        )}

        {customInput.type === "MULTI_SELECTION_DROPDOWN" && (
          <div className="relative inline-block w-full">
            <div className={`pb-1 break-words ${FIELD_LABEL_CLASS}`}>
              {customInput.title}
              {customInput.required && <span className={REQUIRED_MARK_CLASS}>&nbsp;*</span>}
            </div>
            <div className="w-full">
              <MultiSelectDropdown
                options={customInput.options || []}
                selectedValues={customInput.answers || []}
                onChange={
                  measurement
                    ? () => {}
                    : (newValues) => this.handleMultiSelectDropdownAnswerChange(newValues, index)
                }
                placeholder="Select options"
                ariaLabel={customInput.title}
                error={!!fieldErrors[index]}
                brandColors={brandColors}
              />
            </div>
            <AnimatedError error={fieldErrors[index]} />
          </div>
        )}
      </div>
    );
  };

  renderFieldList = (fieldIndexes, { measurement = false } = {}) => (
    <>
      {fieldIndexes.map((index) => {
        const customInput = this.props.customInputsValues[index];

        if (!customInput) {
          return null;
        }

        return (
          <div
            key={`${measurement ? "measurement" : "visible"}-${index}`}
            ref={measurement ? (node) => this.setFieldMeasurementRef(index, node) : undefined}
            className="w-full"
          >
            {this.renderField(customInput, index, { measurement })}
          </div>
        );
      })}
    </>
  );

  getPrimaryButtonLabel = (isLastFieldPage) => {
    if (this.shouldPaginate() && !isLastFieldPage) {
      return "Next";
    }

    if (this.props.primaryButtonLabel) {
      return this.props.primaryButtonLabel;
    }

    // The form's checkout label ("Give $50", "Add to cart"), from getFormCheckoutButtonLabel.
    if (this.props.checkoutButtonLabel) {
      return this.props.checkoutButtonLabel;
    }

    return this.props.addToCart === true ? "Add to cart" : "Give";
  };

  renderActionButtons = ({ isLastFieldPage, ref = undefined } = {}) => {
    return (
      <div ref={ref} className="mt-auto flex flex-col gap-2 pt-3 form-action-buttons">
        <div className="relative overflow-hidden">
          <Button
            type="button"
            family="cta"
            variant="cta"
            size="cta"
            className="dynamic-button-text"
            onClick={this.handleClick}
          >
            {this.getPrimaryButtonLabel(isLastFieldPage)}
          </Button>
        </div>
        {/* Back lives in the title strip; the mobile drawer has no strip of ours, so it stays here. */}
        {this.props.isDrawerOpen && (
          <button
            type="button"
            className="form-back-button"
            onClick={this.handleBackClick}
          >
            Back
          </button>
        )}
      </div>
    );
  };

  renderPageContent = (
    fieldIndexes,
    { isLastFieldPage, bodyRef = undefined, footerRef = undefined } = {},
  ) => (
    <div ref={bodyRef} className="flex flex-1 min-h-0 w-full flex-col">
      <div className="flex flex-col cs-form-sections">{this.renderFieldList(fieldIndexes)}</div>
      {this.renderActionButtons({
        isLastFieldPage,
        ref: footerRef,
      })}
    </div>
  );

  renderPaginatedPage = (fieldIndexes, pageIndex, internalTransitionClassName) => {
    const isActive = this.state.currentFieldPageIndex === pageIndex;

    return (
      <div
        key={`custom-input-page-${fieldIndexes[0] ?? pageIndex}`}
        className={`page ${isActive ? "active" : ""} ${isActive ? internalTransitionClassName : ""}`.trim()}
      >
        {this.renderPageContent(fieldIndexes, {
          isLastFieldPage: pageIndex === this.getResolvedFieldPages().length - 1,
          bodyRef: isActive ? this.pageBodyRef : undefined,
          footerRef: isActive ? this.footerRef : undefined,
        })}
      </div>
    );
  };

  render() {
    const { isDrawerOpen, inline, formDisplayMode } = this.props;
    const {
      transitionClassName = "",
      transitionWrapperStyle,
      pageTransitionDirection,
    } = this.props;
    const shouldPaginate = this.shouldPaginate();
    const fieldPages = shouldPaginate ? this.getResolvedFieldPages() : [];
    const allFieldIndexes = this.getAllFieldIndexes();
    const currentPageFieldIndexes = shouldPaginate
      ? this.getCurrentPageFieldIndexes()
      : allFieldIndexes;
    const externalTransitionClassName =
      this.state.activeTransitionSource === "external"
        ? getPageTransitionClasses(true, pageTransitionDirection, this.props.formDisplayMode)
        : transitionClassName;
    const internalTransitionClassName =
      this.state.activeTransitionSource === "internal" && shouldPaginate
        ? getPageTransitionClasses(true, this.state.internalPageDirection, this.props.formDisplayMode)
        : "";
    const contentWrapperStyle =
      transitionWrapperStyle || {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        height: "100%",
        width: "100%",
      };

    if (inline) {
      return <div className="flex flex-col cs-form-sections w-full">{this.renderFieldList(this.getAllFieldIndexes())}</div>;
    }

    return (
      // The questions step names itself in the same title strip as the first step (owner call
      // 2026-10-21, replacing Paper HBQ-0's in-card heading), framed like every later step.
      <FormStepFrame
        title="Additional information"
        onBack={this.handleBackClick}
        isDrawerOpen={isDrawerOpen}
        formDisplayMode={formDisplayMode}
        bodyClassName={`relative flex flex-col w-full ${!isDrawerOpen && formDisplayMode !== 'overlay' ? 'max-w-[780px] mx-auto' : ''}`}
        bodyStyle={{
          flex: '1 1 auto',
          height: isDrawerOpen ? "100%" : undefined,
          overflow: shouldPaginate ? "hidden" : undefined,
        }}
      >
          <div
            className={externalTransitionClassName}
            style={contentWrapperStyle}
          >
            {shouldPaginate ? (
              <div
                className={`page-container form-page-stack h-full min-h-0 flex-1 ${formDisplayMode === 'dynamic' && !isDrawerOpen ? 'dynamic-page-slide-x' : ''}`}
              >
                {fieldPages.map((fieldIndexes, pageIndex) =>
                  this.renderPaginatedPage(
                    fieldIndexes,
                    pageIndex,
                    internalTransitionClassName,
                  ),
                )}
              </div>
            ) : (
              this.renderPageContent(currentPageFieldIndexes, {
                isLastFieldPage: true,
                bodyRef: this.pageBodyRef,
                footerRef: this.footerRef,
              })
            )}

            {shouldPaginate && (
              <div
                ref={this.measurementRootRef}
                aria-hidden="true"
                className="absolute inset-0 overflow-hidden opacity-0 pointer-events-none"
                style={{
                  visibility: "hidden",
                  zIndex: -1,
                }}
              >
                <div className="w-full px-[12px] py-[12px]">
                  <div className="flex flex-col cs-form-sections">{this.renderFieldList(this.getAllFieldIndexes(), { measurement: true })}</div>
                </div>
              </div>
            )}
          </div>
      </FormStepFrame>
    );
  }
}

export default CustomInputsPage;
